import { useEffect } from 'react';

/**
 * True for the save chord (Cmd+S on mac, Ctrl+S elsewhere).
 *
 * Alt/Shift variants are left alone — they are browser bindings of their own
 * ("Save As…"), so claiming them would break the user's expectation. Held-key
 * repeats are ignored so a leaned-on chord fires one save, not dozens.
 * Exported for unit tests: the suite runs in vitest's `node` environment with
 * no DOM (see vite.config.ts), so the predicate is tested directly rather than
 * through a real keydown.
 */
export function isSaveShortcut(
  e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'key' | 'repeat'>
): boolean {
  if (e.repeat) return false;
  if (!(e.metaKey || e.ctrlKey)) return false;
  if (e.altKey || e.shiftKey) return false;
  return typeof e.key === 'string' && e.key.toLowerCase() === 's';
}

/**
 * Cmd/Ctrl+S saves the owning modal instead of opening the browser's
 * "save page as HTML" dialog.
 *
 * Capture phase so it wins over field-level handlers inside the modal. While
 * the listener is attached the chord is ALWAYS preventDefault'd, even if the
 * caller's callback decides it can't save right now (a save already in
 * flight) — a browser save dialog leaking through mid-save is exactly the
 * failure this exists to stop. So `enabled` means "this modal is open and owns
 * the chord", NOT "a save is currently possible"; keep the can-I-save-yet
 * guard inside `onSave`. Owners that stay mounted while closed must pass their
 * open flag, or they would swallow Cmd+S for the whole app.
 */
export function useSaveShortcut(onSave: () => void, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isSaveShortcut(e)) return;
      e.preventDefault();
      onSave();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onSave, enabled]);
}
